import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export const QUERY_KEYS = {
  dashboard: ['dashboard'] as const,
  analytics: ['analytics'] as const,
  members: ['members'] as const,
  member: (id: string) => ['members', id] as const,
  campaigns: ['campaigns'] as const,
  campaign: (id: string) => ['campaigns', id] as const,
  referrals: ['referrals'] as const,
  referral: (id: string) => ['referrals', id] as const,
  referralNetwork: ['referral-network'] as const,
  settings: ['settings'] as const,
  backoffice: {
    merchants: ['backoffice', 'merchants'] as const,
    users: ['backoffice', 'users'] as const,
  },
  webhookLogs: ['webhook-logs'] as const,
}

export function useDashboard(filters?: {
  startDate?: string
  endDate?: string
  campaignId?: string
}) {
  return useQuery({
    queryKey: [...QUERY_KEYS.dashboard, filters],
    queryFn: () => api.dashboard.get(filters),
  })
}

export function useAnalytics(filters?: {
  startDate?: string
  endDate?: string
  campaignId?: string
  excludeRepurchases?: boolean
}) {
  return useQuery({
    queryKey: [...QUERY_KEYS.analytics, filters],
    queryFn: () => api.analytics.get(filters),
  })
}

export function useWebhookLogs() {
  return useQuery({
    queryKey: QUERY_KEYS.webhookLogs,
    queryFn: () => api.dashboard.getWebhookLogs(),
    refetchInterval: 5000,
  })
}

export function useMembers(
  page = 1,
  limit = 20,
  filters?: { search?: string; status?: string },
  sort?: { sortBy?: string | null; sortDirection?: 'asc' | 'desc' | null }
) {
  return useQuery({
    queryKey: [...QUERY_KEYS.members, { page, limit, ...filters, ...sort }],
    queryFn: () =>
      api.members.list(
        page,
        limit,
        filters?.search,
        filters?.status,
        sort?.sortBy ?? undefined,
        sort?.sortDirection ?? undefined
      ),
    placeholderData: (previousData) => previousData,
  })
}

export function useMember(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.member(id),
    queryFn: () => api.members.get(id),
    enabled: !!id,
  })
}

export function useCampaigns() {
  return useQuery({
    queryKey: QUERY_KEYS.campaigns,
    queryFn: () => api.campaigns.list(),
  })
}

export function useCampaign(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.campaign(id),
    queryFn: () => api.campaigns.get(id),
    enabled: !!id,
  })
}

export function useMerchants() {
  return useQuery({
    queryKey: QUERY_KEYS.backoffice.merchants,
    queryFn: () => api.backoffice.getMerchants(),
  })
}

export function useUsers() {
  return useQuery({
    queryKey: QUERY_KEYS.backoffice.users,
    queryFn: () => api.backoffice.getUsers(),
  })
}

export function useSettings() {
  return useQuery({
    queryKey: QUERY_KEYS.settings,
    queryFn: () => api.settings.get(),
  })
}

export function useReferrals(
  page = 1,
  limit = 20,
  filters?: { search?: string; status?: string; source?: string },
  sort?: { sortBy?: string | null; sortDirection?: 'asc' | 'desc' | null }
) {
  return useQuery({
    queryKey: [...QUERY_KEYS.referrals, { page, limit, ...filters, ...sort }],
    queryFn: () =>
      api.referrals.list(
        page,
        limit,
        filters?.search,
        filters?.status,
        filters?.source,
        sort?.sortBy ?? undefined,
        sort?.sortDirection ?? undefined
      ),
    placeholderData: (previousData) => previousData,
  })
}

export function useReferral(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.referral(id),
    queryFn: () => api.referrals.get(id),
    enabled: !!id,
  })
}

export function useReferralNetwork(filters: { status?: string; minReferrals?: number } = {}) {
  // Default to showing only members with at least 1 referral to avoid overwhelming UI
  const minReferrals = filters.minReferrals ?? 1

  return useQuery({
    queryKey: ['referral-network', filters.status, minReferrals],
    queryFn: () => api.referrals.getNetwork({ status: filters.status, minReferrals }),
  })
}
